import { jsx, jsxs } from "react/jsx-runtime";
import { useOverlay, useOverlayPosition } from "@react-aria/overlays";
import { useLocale } from "@react-aria/i18n";
import { useRangeCalendar } from "@react-aria/calendar";
import { useRangeCalendarState } from "@react-stately/calendar";
import { createCalendar } from "@internationalized/date";
import { mergeProps } from "@react-aria/utils";
import { useRef } from "react";
import "../ThemeProvider/ThemeProvider.mjs";
import { ThemePortal } from "../ThemePortal/ThemePortal.mjs";
import "../ThemeProvider/ThemeContext.mjs";
import "../private/DefaultMessagesContext.mjs";
import "../ThemeProvider/BreakpointContext.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import { PublicBox } from "../Box/Box.mjs";
import { Stack } from "../Stack/Stack.mjs";
import "../Box/BackgroundContext.mjs";
import "../Divider/Divider.css.mjs";
import "../Accordion/AccordionContext.mjs";
import "../Text/TextContext.mjs";
import "../private/defaultTextProps.mjs";
import "../../css/typography.css.mjs";
import "../private/MaxLines/MaxLines.css.mjs";
import "../../css/negativeMargin/negativeMargin.css.mjs";
import "../Columns/ColumnsContext.mjs";
import "../Column/Column.css.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../private/touchable/virtualTouchable.css.mjs";
import "../private/hideFocusRings/hideFocusRings.css.mjs";
import "../Accordion/AccordionItem.css.mjs";
import "../Actions/ActionsContext.mjs";
import "../../css/textAlignedToIcon.css.mjs";
import "../Alert/Alert.css.mjs";
import "../Autosuggest/Autosuggest.mjs";
import "../Avatar/Avatar.css.mjs";
import "../Badge/Badge.mjs";
import "../Breadcrumb/Breadcrumb.css.mjs";
import "../Box/ColoredBox.mjs";
import "../Button/Button.mjs";
import "../ButtonIcon/ButtonIcon.mjs";
import "../ButtonLink/ButtonLink.mjs";
import "../private/Keyline/Keyline.css.mjs";
import "../Checkbox/Checkbox.mjs";
import "../Checkbox/CheckboxStandalone.mjs";
import "../ContentBlock/ContentBlock.css.mjs";
import "../DateField/DateField.mjs";
import "../private/Field/FloatingLabel.css.mjs";
import "../private/Field/Field.css.mjs";
import { CalendarHeader } from "../DateField/CalendarHeader.mjs";
import { calendar } from "../DateField/DateField.css.mjs";
import { CalendarGrid } from "../DateField/CalendarGrid.mjs";
import "../private/Modal/Modal.mjs";
import "../private/Modal/ModalContent.mjs";
import "../private/Modal/ModalContext.mjs";
import "../TextLink/TextLink.mjs";
import "../TextLinkButton/TextLinkButton.css.mjs";
import "../private/AvoidWidowIcon/AvoidWidowIcon.css.mjs";
import "../Dropdown/Dropdown.mjs";
import "../Hidden/Hidden.css.mjs";
import "../HiddenVisually/HiddenVisually.css.mjs";
import "../Link/Link.mjs";
import "../List/List.css.mjs";
import "../Loader/Loader.css.mjs";
import "../Skeleton/Skeleton.css.mjs";
import "../MenuRenderer/MenuRenderer.mjs";
import "../MenuItem/MenuItem.mjs";
import "../MenuItemCheckbox/MenuItemCheckbox.mjs";
import "../MenuItemDivider/MenuItemDivider.mjs";
import "../MenuItem/MenuItemLink.mjs";
import "../OverflowMenu/OverflowMenu.css.mjs";
import "../MonthPicker/MonthPicker.mjs";
import "../Pagination/Pagination.css.mjs";
import "../PasswordField/PasswordField.mjs";
import "../Pincode/Pincode.mjs";
import "../PopoverRenderer/PopoverRenderer.mjs";
import "../Pressable/Pressable.mjs";
import "../RadioGroup/RadioGroup.mjs";
import "../RadioGroup/RadioItem.mjs";
import "../RadioGroup/RadioItemBlock.mjs";
import "../SelectableList/SelectableList.css.mjs";
import "../private/BulkActions/BulkActions.css.mjs";
import "../Spread/Spread.css.mjs";
import "../Stepper/Step.mjs";
import "../Stepper/Stepper.mjs";
import "../Tabs/Tab.mjs";
import "../Table/Table.mjs";
import "../Table/Table.css.mjs";
import "../Tabs/TabsProvider.mjs";
import "../Tabs/TabListContext.mjs";
import "../Tabs/Tabs.css.mjs";
import "../Tabs/TabPanelsContext.mjs";
import "../Tag/Tag.css.mjs";
import "../Textarea/Textarea.mjs";
import "../TextDropdown/TextDropdown.css.mjs";
import "../TextField/TextField.mjs";
import "../Tiles/Tiles.css.mjs";
import "../Toggle/Toggle.mjs";
import "../useToast/ToastContext.mjs";
import "../TooltipRenderer/TooltipRenderer.mjs";
import "../TimeField/DateSegment.css.mjs";
import "../private/FieldButtonIcon/FieldButtonIcon.mjs";
import "../MultiStepModal/MultiStepModal.mjs";
import "../SearchField/SearchField.mjs";
import "../RadioBar/RadioBarContext.mjs";
import "../RadioBar/RadioBar.css.mjs";
import "../../themes/vars.css.mjs";
import "../RadioBar/RadioBar.mjs";
function CalendarRange({
  minValue,
  maxValue,
  inputRef,
  calendarProps,
  shortcuts,
  onClose
}) {
  var _a;
  const overlayRef = useRef(null);
  const { locale } = useLocale();
  const rangeCalendarState = useRangeCalendarState({
    minValue,
    maxValue,
    ...calendarProps,
    locale,
    createCalendar
  });
  const {
    calendarProps: rangeCalendarProps,
    prevButtonProps,
    nextButtonProps
  } = useRangeCalendar({ minValue, maxValue, ...calendarProps }, rangeCalendarState, overlayRef);
  const { overlayProps } = useOverlay(
    {
      isOpen: true,
      isDismissable: true,
      onClose,
      shouldCloseOnInteractOutside: (e) => e.closest('[data-calendar="selector"]') === null
    },
    overlayRef
  );
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: inputRef,
    overlayRef,
    placement: "bottom end",
    offset: 35,
    isOpen: true,
    shouldFlip: true
  });
  (_a = positionProps.style) == null ? true : delete _a.zIndex;
  return /* @__PURE__ */ jsx(ThemePortal, { children: /* @__PURE__ */ jsx(
    PublicBox,
    {
      className: calendar,
      borderRadius: "small",
      ...mergeProps(overlayProps, positionProps),
      ref: overlayRef,
      boxShadow: "medium",
      padding: "xsmall",
      zIndex: "notification",
      role: "dialog",
      ...rangeCalendarProps,
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", align: "center", children: [
        /* @__PURE__ */ jsx(
          CalendarHeader,
          {
            state: rangeCalendarState,
            prevButtonProps,
            nextButtonProps
          }
        ),
        /* @__PURE__ */ jsx(CalendarGrid, { state: rangeCalendarState, shortcuts, type: "range" })
      ] })
    }
  ) });
}
export {
  CalendarRange
};
